import { createApiClient } from './client'

// Auth API endpoints
export interface LoginCredentials {
  email: string
  password: string
}

export interface SignupCredentials {
  email: string
  password: string
  user_metadata?: Record<string, any>
  trip_id?: string
}

export interface AuthResponse {
  access_token: string
  refresh_token: string
  user: {
    id: string
    email: string
    user_metadata?: Record<string, any>
  }
}

export interface RefreshTokenResponse {
  access_token: string
  refresh_token: string
}

export interface UserProfile {
  id: string
  email: string
  user_metadata?: Record<string, any>
  created_at?: string
  updated_at?: string
  profile_image_path?: string | null
  onboarding_completed_at?: string | null
}

export interface SignupResponse {
  success: boolean
  data?: UserProfile
  message?: string
  requiresEmailConfirmation?: boolean
  access_token?: string
  refresh_token?: string
  user?: {
    id: string
    email: string
    user_metadata?: Record<string, any>
  }
}

// Auth API class
export class AuthApi {
  private apiClient: ReturnType<typeof createApiClient>

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  // Login user
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    return this.apiClient.post<AuthResponse>('/auth/login', credentials)
  }

  // Signup user
  async signup(credentials: SignupCredentials): Promise<SignupResponse> {
    return this.apiClient.post<SignupResponse>('/auth/signup', credentials)
  }

  // Refresh access token
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    return this.apiClient.post<RefreshTokenResponse>('/auth/refresh', {
      refresh_token: refreshToken
    })
  }

  // Get current user profile
  async getProfile(): Promise<UserProfile> {
    const response = await this.apiClient.get<{success: boolean, data: UserProfile}>('/auth/me')
    return response.data
  }

  // Update user metadata (e.g., full_name)
  async updateUserMetadata(metadata: { full_name?: string; [key: string]: unknown }): Promise<UserProfile> {
    const response = await this.apiClient.put<{success: boolean, data: UserProfile}>('/auth/me', metadata)
    return response.data
  }

  // Mark the server-side onboarding flag complete. Idempotent.
  async markOnboardingComplete(): Promise<UserProfile> {
    const response = await this.apiClient.post<{success: boolean, data: UserProfile}>('/auth/me/onboarding-complete')
    return response.data
  }

  // Logout user
  async logout(): Promise<void> {
    return this.apiClient.post<void>('/auth/logout')
  }

  // Change password
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return this.apiClient.post<void>('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    })
  }

  // Request password reset email (Supabase sends the link; backend wraps resetPasswordForEmail)
  async requestPasswordReset(email: string): Promise<{ success: boolean; message?: string }> {
    return this.apiClient.post<{ success: boolean; message?: string }>('/auth/reset-password', { email })
  }
}
